"use client";
import { FC, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getDefaultCasinoGamesPayload,
  getGameDesktopSupportFilterString,
  getGameFilterString,
  getGameMobileSupportFilterString,
} from "@/fe-core/helpers/casino";
import {
  getGamesRequest,
  setGameCardTooltip,
} from "@/fe-core/_redux/actions/casinoActions";
import GameCard from "@/components/CasinoPage/GameCard";
import {
  casinoSelector,
  countSelector,
  gamesSelector,
} from "@/fe-core/_redux/selectors/casinoSelectors";
import { ICasinoGameViewModel } from "@/fe-core/meta/interfaces/casino";
import { useDebounce } from "use-debounce";
import { useTranslations } from "next-intl";
import { I18N_NAMESPACE } from "@/config/i18n";
import SearchModal from "../SearchModal";
import SVGIcon from "../SVGIcon";
import GamesGroup from "../GamesGroup";
import EmptyState from "../EmptyState";
import Image from "next/image";
import { localStorageKeys } from "@/fe-core/meta/types/localStorage";
import { usePathname } from "../../navigation";
import { useScreen } from "../../context/ScreenContext";
import InfiniteScroll from "react-infinite-scroll-component";
import useTracking from "../Hooks/useTracking";

type CasinoSearchModalProps = {
  onClose: () => void;
  isOpen: boolean;
};

const CasinoSearchModal: FC<CasinoSearchModalProps> = ({ onClose, isOpen }) => {
  const { click, track } = useTracking();
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [searchHistory, setSearchHistory] = useState<string[]>([]);
  const [searchResult, setSearchResult] = useState<ICasinoGameViewModel[]>([]);
  const dispatch = useDispatch();
  const pathname = usePathname() ?? "";
  const t = useTranslations(I18N_NAMESPACE.SEARCH);
  const { isDesktop } = useScreen();
  const games: ICasinoGameViewModel[] = useSelector(gamesSelector);
  const totalGames = useSelector(countSelector);
  const isLoading: boolean = useSelector(casinoSelector).pending;
  const [debouncedSearchTerm] = useDebounce<string>(searchTerm, 1000);

  useEffect(() => {
    dispatch(setGameCardTooltip(null));
  }, [pathname]);

  useEffect(() => {
    setSearchHistory(
      JSON.parse(
        localStorage.getItem(localStorageKeys.SEARCH_HISTORY) as string
      ) || []
    );
  }, []);
  const handleSearchHistory = (_searchTerm: string): void => {
    if (_searchTerm) {
      const updatedHistory = [
        _searchTerm,
        ...searchHistory.filter((item) => item !== _searchTerm),
      ].slice(0, 10);
      localStorage.setItem(
        localStorageKeys.SEARCH_HISTORY,
        JSON.stringify(updatedHistory)
      );
      setSearchHistory(updatedHistory);
      track(`search games`, {
        searchTexts: _searchTerm,
        searchHistory: updatedHistory,
        component: "searchModal - CasinoSearchModal",
      });
    }
  };

  useEffect(() => {
    const payload = getDefaultCasinoGamesPayload();

    if (debouncedSearchTerm) {
      payload.filters.push(getGameFilterString(debouncedSearchTerm));
    }
    if (debouncedSearchTerm.length >= 3) {
      setSearchHistory([]);
      payload.pagination.offset = 0;

      if (isDesktop)
        payload.filters.push(getGameDesktopSupportFilterString(true));
      else payload.filters.push(getGameMobileSupportFilterString(true));
      dispatch(getGamesRequest(payload));
      handleSearchHistory(debouncedSearchTerm);
    }
  }, [debouncedSearchTerm, dispatch]);

  useEffect(() => {
    setSearchResult(games);
  }, [games]);
  const loadResultGame = () => {
    const payload = getDefaultCasinoGamesPayload();
    payload.filters.push(getGameFilterString(debouncedSearchTerm));
    payload.pagination.offset = games.length;
    if (isDesktop)
      payload.filters.push(getGameDesktopSupportFilterString(true));
    else payload.filters.push(getGameMobileSupportFilterString(true));
    dispatch(getGamesRequest(payload));
  };

  const removeSingleHistory = (
    e: React.MouseEvent<HTMLDivElement>,
    _item: string
  ) => {
    e.stopPropagation();
    localStorage.setItem(
      localStorageKeys.SEARCH_HISTORY,
      JSON.stringify(
        searchHistory && searchHistory.filter((item) => item !== _item)
      )
    );
    setSearchHistory((prevHistory) =>
      prevHistory.filter((prevItem) => prevItem !== _item)
    );
  };
  const clearSearchHistory = () => {
    localStorage.removeItem(localStorageKeys.SEARCH_HISTORY);
    setSearchHistory([]);
    click(`Clear Search History`, {
      label: t("clear-search-cta-btn"),
      component: "Sortoptions - CasinoSearchModal",
    });
  };
  const searchHistorySection = () => {
    return (
      <>
        {searchHistory.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <p className="text-base font-normal text-tertiary dark:text-tertiary-50">
                {t("search-history-header")}
              </p>
              <button
                className="text-sm font-bold text-tertiary/70 dark:text-tertiary-50/70"
                onClick={clearSearchHistory}
              >
                {t("clear-search-cta-btn")} ({searchHistory.length})
              </button>
            </div>
            <div className="flex flex-wrap gap-4">
              {searchHistory.map((_item, i) => (
                <button
                  className="!gap-2 rounded btn btn--secondary btn--sm  "
                  key={i}
                  onClick={() => setSearchTerm(_item)}
                >
                  {_item}
                  <div
                    onClick={(e) => removeSingleHistory(e, _item)}
                    className="relative z-50"
                  >
                    <SVGIcon
                      icon="close"
                      className="w-3 h-3 text-tertiary/50 dark:text-white/70"
                    />
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
      </>
    );
  };

  return (
    <SearchModal
      className="flex flex-col justify-between gap-8"
      isOpen={isOpen}
      onClose={() => {
        setSearchTerm("");
        onClose();
      }}
    >
      <div
        className={
          "px-4 border-primary-300/50   bg-tertiary-100 dark:bg-tertiary-900 py-2.5 border rounded flex items-center gap-3"
        }
      >
        <span>
          <SVGIcon
            icon="search"
            className="w-5 h-5 text-tertiary/25 dark:text-tertiary-50/25"
          />
        </span>
        <input
          className={`w-full p-0 text-base sm:text-sm font-normal bg-transparent border-none leading-5.5 text-tertiary focus:shadow-none focus:ring-0 dark:placeholder:text-tertiary-50/50 dark:text-white autofill:!bg-transparent`}
          type="text"
          id="search"
          placeholder={t("gamename-provider")}
          onChange={(e) => setSearchTerm(e.target.value)}
          value={searchTerm}
          autoFocus={true}
        />
        <div className="flex items-center gap-3">
          {/* {searchTerm && (
            <button
              onClick={() => {
                setSearchTerm("");
                click(`Clear Search Text`, {
                  label: "close icon",
                  component: "searchModal - CasinoSearchModal",
                });
              }}
            >
              <SVGIcon
                icon="close"
                className="w-4 h-4 text-tertiary/50 dark:text-tertiary-50/50"
              />
            </button>
          )} */}
          <button
            className="text-sm font-normal text-tertiary dark:text-tertiary-50"
            onClick={() => {
              setSearchTerm("");
              onClose();
            }}
          >
            {/* {t("cancel-search-cta-btn")} */}
            <SVGIcon
              icon="close"
              className="w-4 h-4 text-tertiary/50 dark:text-tertiary-50/50"
            />
          </button>
        </div>
      </div>
      {debouncedSearchTerm && debouncedSearchTerm.length >= 3 ? (
        isLoading && searchResult?.length <= 0 ? (
          <div className="relative flex justify-center h-10">
            <Image
              src="/img/icons/loading-dots.svg"
              fill
              alt="spinner"
              className=""
              loading="lazy"
            />
          </div>
        ) : searchResult?.length > 0 ? (
          <div>
            <div className="flex items-center justify-between mb-3 sm:mb-4">
              <p className="text-base font-normal text-tertiary dark:text-tertiary-50">
                {t("search-result")}
              </p>
              {/* <div className="flex justify-center">
                {games?.length < totalGamesCount && (
                  <button
                    className="text-sm font-normal text-primary-500 dark:text-primary-200"
                    onClick={loadMoreGames}
                  >
                    {t("about-results", {
                      GAMES_COUNT: totalGamesCount - games.length,
                    })}
                  </button>
                )}
              </div> */}
            </div>
            <div
              id="scrollableDiv"
              className="max-h-[460px] xl:max-h-[345px] overflow-auto h-full"
            >
              <InfiniteScroll
                dataLength={searchResult?.length}
                next={loadResultGame}
                hasMore={searchResult?.length < totalGames}
                loader={
                  isLoading && (
                    <div className="relative flex justify-center h-10">
                      <Image
                        src="/img/icons/loading-dots.svg"
                        fill
                        alt="spinner"
                        className=""
                        loading="lazy"
                      />
                    </div>
                  )
                }
                scrollableTarget="scrollableDiv"
                className="!overflow-hidden"
              >
                <div className="grid grid-cols-2 rounded gap-y-3 gap-x-3 sm:grid-cols-4 lg:grid-cols-6 overflow-hidden">
                  {searchResult?.map((game, index) => {
                    return (
                      <GameCard
                        game={game}
                        key={`game__${index}`}
                        hidePlayButton={true}
                      />
                    );
                  })}
                </div>
              </InfiniteScroll>
            </div>
          </div>
        ) : (
          <EmptyState title={t("no-games-available")} />
        )
      ) : (
        <>
          {/* {vendorsSection()} */}
          <p className="text-sm font-normal text-center text-tertiary dark:text-tertiary-50">
            {t("search-required-characters")}
          </p>
        </>
      )}
      {searchHistorySection()}
      <GamesGroup
        title={t("recommended-for-you")}
        className="-mx-[15px]"
        isSearchSlider={true}
        hidePlayButton={true}
      />
    </SearchModal>
  );
};
export default CasinoSearchModal;
